/** @format */

import React, { useEffect, useState, Suspense } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import axios from 'axios';

import {
	FaBookReader,
	FaUserGraduate,
	FaStar,
	FaChevronRight,
} from 'react-icons/fa';

import Header from '../../components/common/Header';
import { ROUTES } from '../../constant/routes';

import { useSEO } from '../../hooks/useSEO';
import { injectJsonLd } from '../../utils/jsonLd';
import { createSubjectDetailSchema } from '../../schema/subjectDetail.schema';

const EnrollBox = React.lazy(() => import('../../components/ui/EnrollBox'));
const Footer = React.lazy(() => import('../../components/common/Footer'));
const FAQ = React.lazy(() => import('../../components/ui/FAQ'));
const TestimonialSection = React.lazy(
	() => import('../../components/ui/TestimonialSection')
);
const WhatsInside = React.lazy(() => import('../../components/ui/WhatsInside'));
const QuestionList = React.lazy(
	() => import('../../components/ui/QuestionList')
);

/* =======================
   Types
======================= */

interface Subject {
	title: string;
	code: string;
	shortDesc: string;
	fullDescription: string;
	subHeadline1?: string;
	subHeadline2?: string;
	problem?: string;
	solution?: string;
	referenceImage?: string;
	stats: {
		rating: number;
		students: number;
		questions: string;
	};
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

/* =======================
   Component
======================= */

const SubjectDetail: React.FC = () => {
	const { slug } = useParams<{ slug: string }>();
	const location = useLocation();

	const [subjectId, setSubjectId] = useState<string | null>(
		location.state?.subjectId || null
	);
	const [subjectCode, setSubjectCode] = useState<string | null>(
		location.state?.subjectCode || null
	);
	const [basicInfo, setBasicInfo] = useState<{
		title?: string;
		description?: string;
	}>({
		title: location.state?.subjectName,
		description: location.state?.description,
	});

	const [subject, setSubject] = useState<Subject | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const pageUrl = slug
		? `https://www.llb360.com/law-subjects/${slug}`
		: 'https://www.llb360.com/law-subjects';

	const pageTitle = basicInfo.title
		? `${basicInfo.title} for LLB Students | LLB360`
		: 'LLB Law Subject | LLB360';

	/* =======================
     SEO (ALWAYS CALLED)
  ======================= */

	useSEO({
		title: pageTitle,
		description: basicInfo.description
			? basicInfo.description
			: subject?.shortDesc || 'LLB law subject details',
		keywords: subject
			? `${subject.title}, ${subject.title} notes, ${subject.title} case law, ${subject.title} LLB preparation`
			: 'LLB subjects, law subjects',
		canonical: pageUrl,

		ogTitle: pageTitle,
		ogDescription: basicInfo.description
			? basicInfo.description
			: subject?.shortDesc || 'LLB law subject details',
		ogSiteName: 'LLB 360',
		ogImage: 'https://www.llb360.com/og-law-subjects.webp',

		twitterTitle: pageTitle,
		twitterDescription: basicInfo.description
			? basicInfo.description
			: subject?.shortDesc || 'LLB law subject details',
		twitterImage: 'https://www.llb360.com/og-law-subjects.webp',
	});

	// Resolve slug
	useEffect(() => {
		if (subjectId || !slug) return;

		const resolveSlug = async () => {
			try {
				const res = await axios.get(`${API_BASE_URL}/subjectallsubjects`);
				const match = res.data?.success?.find(
					(s: any) =>
						s.subjectName.toLowerCase().replace(/[^a-z0-9]+/g, '-') === slug
				);

				if (!match) throw new Error('Not found');

				setSubjectId(match.subjectId);
				setSubjectCode(match.subjectCode);
				setBasicInfo({
					title: match.subjectName,
					description: match.description,
				});
			} catch {
				setError('Subject not found');
				setLoading(false);
			}
		};

		resolveSlug();
	}, [slug, subjectId]);

	// Fetch subject details
	useEffect(() => {
		if (!subjectId) return;

		const fetchData = async () => {
			try {
				const { data } = await axios.get(
					`${API_BASE_URL}/seo?subjectId=${subjectId}`
				);

				const raw = Array.isArray(data?.success)
					? data.success[0]
					: data.success;

				setSubject({
					title: raw.title,
					code: raw.subjectId || 'LAW',
					shortDesc: raw.subHeadline1,
					fullDescription: raw.problem,
					subHeadline1: raw.subHeadline1,
					subHeadline2: raw.subHeadline2,
					problem: raw.problem,
					solution: raw.solution,
					referenceImage: raw.referenceImage,
					stats: {
						rating: 4.3,
						students: 1000,
						questions: '40+ Questions',
					},
				});
			} catch {
				setError('Failed to load subject');
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [subjectId]);

	// Inject JSON-LD
	useEffect(() => {
		if (!subject || !basicInfo.title) return;

		injectJsonLd(
			createSubjectDetailSchema({
				subjectName: basicInfo.title,
				subjectDescription: basicInfo.description,
				pageUrl,
			})
		);
	}, [subject, basicInfo, pageUrl]);

	if (loading) {
		return (
			<div className='min-h-screen flex items-center justify-center'>
				<div className='animate-pulse text-gray-500 text-lg'>Loading...</div>
			</div>
		);
	}

	if (!subject || error) {
		return (
			<body className='bg-white p-0! overflow-x-hidden'>
				<Header />
				<main className='min-h-[60vh] flex items-center justify-center'>
					<div className='text-center'>
						<h2 className='text-3xl font-bold text-myButtonBg'>Subject Not Found</h2>
						<p className='text-gray-600 mt-2'>
							The subject you are looking for does not exist.
						</p>
						<Link
							to={ROUTES.ALL_SUBJECTS}
							className='inline-block mt-4 text-gray-700 font-medium'>
							← Back to Subjects
						</Link>
					</div>
				</main>
				<Footer />
			</body>
		);
	}

	return (
		<body className='bg-white p-0! overflow-x-hidden'>
			<Header />

			<main>
				{/* Hero */}
				<section className='bg-gray-900 text-white py-20'>
					<div className='container max-w-5xl mx-auto px-4'>
						<nav
							className='flex items-center text-sm text-gray-400 mb-8'
							aria-label='Breadcrumb'>
							<Link
								to='/'
								className='hover:text-white transition-colors'>
								Home
							</Link>
							<FaChevronRight className='mx-2 w-3 h-3' />
							<Link
								to={ROUTES.ALL_SUBJECTS}
								className='hover:text-white transition-colors'>
								Subjects
							</Link>
							<FaChevronRight className='mx-2 w-3 h-3' />
							<span
								className='text-white font-medium truncate'
								aria-current='page'>
								{basicInfo.title || subject.title}
							</span>
						</nav>

						<div className='flex flex-col md:flex-row gap-8'>
							<div className='flex-1 space-y-4'>
								<span className='bg-white/20 px-3 py-1 rounded-full text-xs uppercase'>
									{subject.code}
								</span>

								<h1 className='text-4xl font-extrabold'>
									{basicInfo.title || subject.title}
								</h1>

								<p className='text-lg text-white/90'>
									{basicInfo.description || subject.shortDesc}
								</p>

								<div className='flex gap-6 text-sm'>
									<div className='flex items-center gap-2'>
										<FaStar className='text-yellow-400' />
										{subject.stats.rating}
									</div>
									<div className='flex items-center gap-2'>
										<FaUserGraduate />
										{subject.stats.students}+ Students
									</div>
									<div className='flex items-center gap-2'>
										<FaBookReader />
										{subject.stats.questions}
									</div>
								</div>
							</div>

							<EnrollBox />
						</div>
					</div>
				</section>

				{/* Content */}
				<section className='container max-w-5xl mx-auto px-4 py-12 space-y-12'>
					<div className='space-y-4'>
						<h2 className='text-2xl font-bold text-myButtonBg'>
							{subject.title}
						</h2>
						<p className='text-gray-600 mt-2'>{subject.subHeadline1}</p>
					</div>

					<WhatsInside />

					<div className='space-y-4'>
						<h3 className='text-xl font-bold text-myButtonBg'>
							{subject.subHeadline2}
						</h3>
						<p className='text-gray-600'>{subject.problem}</p>
						<p className='text-gray-600'>{subject.solution}</p>
					</div>

					{subject.referenceImage && (
						<img
							src={subject.referenceImage}
							alt={subject.title}
							className='rounded-lg'
							loading='lazy'
						/>
					)}

					<QuestionList subjectCode={subjectCode || undefined} />
				</section>

				<Suspense fallback={null}>
					<FAQ subjectId={subjectId || undefined} />
					<TestimonialSection />
				</Suspense>
			</main>
			<Suspense fallback={null}>
				<Footer />
			</Suspense>
		</body>
	);
};

export default SubjectDetail;
